import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SeoManager, getPageSeo, buildFAQSchema } from '../seo';
import FAQSection, { colorMemoryFaqs } from '../components/FAQSection';
import {
  colorDistance,
  getColorMemoryRank,
  hslToHex,
  hslToRgb,
  randomColor,
  scoreFromDistance,
} from '../utils/colorUtils';
import '../styles/ColorMemory.css';

const HIGHSCORE_KEY = 'cm-color-memory-highscore';
const SHOW_SECONDS = 5;

export default function ColorMemoryPage() {
  const [phase, setPhase] = useState('idle');
  const [target, setTarget] = useState(() => randomColor());
  const [guess, setGuess] = useState({ h: 0, s: 50, l: 50 });
  const [countdown, setCountdown] = useState(SHOW_SECONDS);
  const [score, setScore] = useState(null);
  const [distance, setDistance] = useState(null);
  const [highscore, setHighscore] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    try {
      const stored = Number(localStorage.getItem(HIGHSCORE_KEY));
      if (Number.isFinite(stored) && stored > 0) setHighscore(stored);
    } catch {
      // Ignore private-mode storage errors
    }
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const saveHighscore = useCallback((nextScore) => {
    setHighscore((best) => {
      if (nextScore <= best) return best;
      try {
        localStorage.setItem(HIGHSCORE_KEY, String(nextScore));
      } catch {
        // Ignore private-mode storage errors
      }
      return nextScore;
    });
  }, []);

  const startRound = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setTarget(randomColor());
    setGuess({ h: 0, s: 50, l: 50 });
    setScore(null);
    setDistance(null);
    setCountdown(SHOW_SECONDS);
    setPhase('showing');

    let remaining = SHOW_SECONDS;
    timerRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setPhase('picking');
      }
    }, 1000);
  }, []);

  const submitGuess = useCallback(() => {
    const targetRgb = hslToRgb(target.h, target.s, target.l);
    const guessRgb = hslToRgb(guess.h, guess.s, guess.l);
    const dist = colorDistance(targetRgb, guessRgb);
    const nextScore = scoreFromDistance(dist);

    setDistance(dist);
    setScore(nextScore);
    saveHighscore(nextScore);
    setPhase('result');
  }, [guess, saveHighscore, target]);

  const targetHex = hslToHex(target.h, target.s, target.l);
  const guessHex = hslToHex(guess.h, guess.s, guess.l);
  const rank = score != null ? getColorMemoryRank(score) : null;
  const seo = getPageSeo('color-memory');
  const structuredData = seo?.structuredData
    ? [seo.structuredData, buildFAQSchema(colorMemoryFaqs)]
    : buildFAQSchema(colorMemoryFaqs);

  return (
    <>
      <SeoManager
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        structuredData={structuredData}
      />

      <section className="cm-game" aria-label="Color memory game">
        <h1 className="cm-visually-hidden">Color Memory: Guess the Color</h1>

        <header className="cm-header">
          <div className="cm-score-block">
            <span className="cm-score-number">{highscore}</span>
            <span className="cm-score-label">Best Score</span>
          </div>
          {score != null && (
            <div className="cm-score-block">
              <span className="cm-score-number">{score}</span>
              <span className="cm-score-label">Last Score</span>
            </div>
          )}
        </header>

        <div className="cm-stage">
          {phase === 'idle' && (
            <div className="cm-idle-card">
              <h2>Color Memory</h2>
              <p>
                A color appears for {SHOW_SECONDS} seconds. Memorize it, then
                use the sliders to recreate it as closely as you can. Submit
                your guess to see how close you were and get a score out of 100.
              </p>
              <button type="button" className="cm-btn cm-btn-primary" onClick={startRound}>
                Start Round
              </button>
            </div>
          )}

          {phase === 'showing' && (
            <div
              className="cm-show-panel"
              style={{ backgroundColor: targetHex }}
              aria-label="Memorize this color"
            >
              <span className="cm-show-label">Memorize</span>
              <span className="cm-countdown" aria-live="polite">{countdown}</span>
            </div>
          )}

          {phase === 'picking' && (
            <div className="cm-picker-card">
              <h2>Recreate the Color</h2>
              <p>Adjust hue, saturation, and lightness to match what you remember.</p>
              <div
                className="cm-preview"
                style={{ backgroundColor: guessHex }}
                aria-hidden="true"
              />
              <div className="cm-slider-group">
                <label htmlFor="cm-hue">
                  <span>Hue</span>
                  <span>{guess.h}°</span>
                </label>
                <input
                  id="cm-hue"
                  type="range"
                  min="0"
                  max="359"
                  value={guess.h}
                  onChange={(event) => setGuess((prev) => ({ ...prev, h: Number(event.target.value) }))}
                />
              </div>
              <div className="cm-slider-group">
                <label htmlFor="cm-sat">
                  <span>Saturation</span>
                  <span>{guess.s}%</span>
                </label>
                <input
                  id="cm-sat"
                  type="range"
                  min="0"
                  max="100"
                  value={guess.s}
                  onChange={(event) => setGuess((prev) => ({ ...prev, s: Number(event.target.value) }))}
                />
              </div>
              <div className="cm-slider-group">
                <label htmlFor="cm-light">
                  <span>Lightness</span>
                  <span>{guess.l}%</span>
                </label>
                <input
                  id="cm-light"
                  type="range"
                  min="0"
                  max="100"
                  value={guess.l}
                  onChange={(event) => setGuess((prev) => ({ ...prev, l: Number(event.target.value) }))}
                />
              </div>
              <button type="button" className="cm-btn cm-btn-primary" onClick={submitGuess}>
                Submit Guess
              </button>
            </div>
          )}

          {phase === 'result' && score != null && (
            <div className="cm-result-card">
              <h2>Results</h2>
              <p className="cm-result-score">{score}/100</p>
              <div className="cm-rank-row">
                <span className="cm-rank-label">Your Rank:</span>
                <span className="cm-rank-animal">{rank.animal}</span>
              </div>
              <p className="cm-result-label">{rank.message}</p>
              <div className="cm-compare">
                <div>
                  <div className="cm-swatch" style={{ backgroundColor: targetHex }} />
                  <p className="cm-swatch-caption">Target</p>
                </div>
                <span className="cm-compare-arrow" aria-hidden="true">→</span>
                <div>
                  <div className="cm-swatch" style={{ backgroundColor: guessHex }} />
                  <p className="cm-swatch-caption">Your guess</p>
                </div>
              </div>
              <p className="cm-result-meta">
                Color distance: {Math.round(distance)} (lower is closer)
              </p>
              <button type="button" className="cm-btn cm-btn-primary" onClick={startRound}>
                Play Again
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="cm-extras">
        <h2>How to Play Color Memory</h2>
        <p>
          Study the color while the countdown runs. When it disappears, rebuild
          it with the HSL sliders and submit. Your score is based on how close
          your RGB values are to the original color.
        </p>
        <ul>
          <li>You have {SHOW_SECONDS} seconds to memorize each color.</li>
          <li>There is no time limit while picking — focus on accuracy.</li>
          <li>Scores range from 0 to 100. Your best score is saved in this browser.</li>
        </ul>
      </section>

      <FAQSection mode="colorMemory" />
    </>
  );
}
